/**
 * Get all technicians' payrate and credits
 * Expected response format:
 * [
 *   {
 *     tech_id: "123",
 *     basic_pay_rate: 150.00,
 *     leave_credits: 5.0,
 *     timestamp_create: "2026-01-01T00:00:00Z",
 *     timestamp_update: "2026-01-22T00:00:00Z"
 *   },
 *   ...
 * ]
 */
export async function getTechniciansPayrateCredits() {
  try {
    const response = await fetch(
      "https://fjphgsx891.execute-api.ap-southeast-1.amazonaws.com/default/boazGetTechniciansPayrateCredits",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error(
        `Error: Failed to fetch technician payrate credits. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "Error: Network or other error occurred while fetching technician payrate credits.",
      error
    );
    return null;
  }
}

/**
 * Get a specific technician's payrate and credits by ID
 * Expected response format:
 * {
 *   tech_id: "123",
 *   basic_pay_rate: 150.00,
 *   leave_credits: 5.0,
 *   timestamp_create: "2026-01-01T00:00:00Z",
 *   timestamp_update: "2026-01-22T00:00:00Z"
 * }
 */
export async function getTechnicianPayrateCredits(techId) {
  try {
    const response = await fetch(
      `https://rbv2afe3l8.execute-api.ap-southeast-1.amazonaws.com/default/boazGetTechniciansPayrateCreditsById?id=${techId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      console.error(
        `Error: Failed to fetch technician payrate credits. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "Error: Network or other error occurred while fetching technician payrate credits.",
      error
    );
    return null;
  }
}

/**
 * Create new technician's payrate and credits record
 * Parameters:
 * - data: { tech_id: string, basic_pay_rate: number, leave_credits: number }
 * 
 * Expected response format:
 * {
 *   message: "Technician payrate record created successfully",
 *   tech_id: string
 * }
 */
export async function postTechnicianPayrateCredits(data) {
  try {
    const payload = {
      tech_id: data.tech_id,
      basic_pay_rate: data.basic_pay_rate || 0,
      leave_credits: data.leave_credits || 5,
    };

    const response = await fetch(
      "https://aw49rqxj2a.execute-api.ap-southeast-1.amazonaws.com/default/boazPostTechniciansPayrateCredits",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      console.error(
        `Error: Failed to create technician payrate credits. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "Error: Network or other error occurred while creating technician payrate credits.",
      error
    );
    return null;
  }
}

/**
 * Update technician's payrate and credits (PATCH method)
 * Parameters:
 * - techId: technician ID
 * - data: { basic_pay_rate: number, leave_credits: number }
 * 
 * Expected response format:
 * {
 *   message: "Updated successfully",
 *   data: { tech_id, basic_pay_rate, leave_credits, timestamp_update }
 * }
 */
export async function patchTechnicianPayrateCredits(techId, data) {
  try {
    const payload = {
      tech_id: techId.toString(),
      basic_pay_rate: data.basic_pay_rate,
      leave_credits: data.leave_credits,
    };

    const response = await fetch(
      `https://su1376xl64.execute-api.ap-southeast-1.amazonaws.com/default/boazPatchTechniciansPayrateCredits`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      console.error(
        `Error: Failed to update technician payrate credits. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "Error: Network or other error occurred while updating technician payrate credits.",
      error
    );
    return null;
  }
}

/**
 * Deduct SIL credits for a technician
 * Parameters:
 * - techId: technician ID
 * - creditsUsed: number of credits to deduct
 * 
 * Expected response format:
 * {
 *   message: "SIL credits deducted successfully",
 *   remaining_credits: 3.5
 * }
 */
export async function deductSILCredits(techId, creditsUsed) {
  try {
    const payload = {
      tech_id: techId.toString(),
      credits_used: creditsUsed,
    };

    const response = await fetch(
      `https://h13aa5fzua.execute-api.ap-southeast-1.amazonaws.com/default/boazPatchDeductSILCredits`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
      return await response.json();
    } else {
      console.error(
        `Error: Failed to deduct SIL credits. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    console.error(
      "Error: Network or other error occurred while deducting SIL credits.",
      error
    );
    return null;
  }
}
